import type { ScreenContext } from './types.ts';
import { createContinueButton } from '@/components/continue-button.ts';
import { createCountdownTimer } from '@/components/countdown-timer.ts';
import { getClipForIndex } from './helpers.ts';

export function renderClipPlay(container: HTMLElement, ctx: ScreenContext) {
  const { state, dispatch, audioManager, clipUrlMap } = ctx;
  const clip = getClipForIndex(state);

  if (!clip) {
    container.innerHTML = '<div class="card">Clip not found.</div>';
    return;
  }

  // Stop any existing playback when entering
  audioManager.stop();

  container.innerHTML = `
    <div class="card space-y-6">
      <div class="space-y-1">
        <p class="text-sm uppercase tracking-wide text-gray-500">Clip ${state.clipIndex}</p>
        </p>
      </div>
      <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
        <div class="flex items-center justify-center gap-2 text-blue-800 mb-4">
          <div class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
          <span class="text-sm font-medium">Playing 30 seconds</span>
        </div>
        <div id="timer-slot" class="my-4"></div>
        <p class="text-sm text-blue-900 text-center">Keep breathing naturally. Just notice how the music lands.</p>
      </div>
      <div id="action-slot"></div>
      <p class="text-xs text-center text-gray-500">The button unlocks after the clip finishes.</p>
    </div>
  `;

  const btn = createContinueButton('Continue', { disabled: true });
  container.querySelector('#action-slot')?.appendChild(btn);

  const enableContinue = () => {
    btn.disabled = false;
    btn.focus();
  };

  const timer = createCountdownTimer(clip.durationSec || 30, () => enableContinue());
  container.querySelector('#timer-slot')?.appendChild(timer.root);
  timer.start();

  btn.addEventListener('click', () => {
    audioManager.stop();
    dispatch({ type: 'NEXT_PHASE' });
  });

  const startPlayback = async () => {
    const url = clipUrlMap.get(clip.id) ?? clip.filename;
    try {
      if (!audioManager.getAudio(clip.id)) {
        await audioManager.preload(url, clip.id);
      }
      const audio = audioManager.getAudio(clip.id)!;
      audio.onended = () => enableContinue();
      await audioManager.play(clip.id);
    } catch (error) {
      console.error('Audio failed to play', error);
      const retry = document.createElement('button');
      retry.className = 'btn-secondary w-full';
      retry.textContent = 'Tap to play clip';
      retry.addEventListener('click', () => {
        retry.remove();
        startPlayback();
      });
      container.querySelector('#action-slot')?.appendChild(retry);
    }
  };

  startPlayback();
}
